/**
 * API client for VocabMaster backend.
 * Handles all HTTP requests to the Lambda API.
 */

import axios from 'axios';
import config from '../config/config';
import cognitoService from './cognito';
import { getCurrentTestUser } from '../config/testUsers';

class ApiClient {
  constructor() {
    this.baseURL = config.apiUrl;
    this.maxRetries = 3;
    this.retryDelay = 1000; // 1 second base delay

    // Create axios instance with default config
    this.client = axios.create({
      baseURL: this.baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000, // 10 seconds timeout
    });

    // Add request interceptor to include authentication
    this.client.interceptors.request.use(
      async (requestConfig) => {
        if (config.features.authEnabled) {
          // Production mode: use Cognito JWT token ONLY
          try {
            const token = await cognitoService.getIdToken();
            requestConfig.headers['Authorization'] = `Bearer ${token}`;
          } catch (error) {
            console.error('Failed to get auth token:', error);
            // Reject the request if we can't get auth token in production
            return Promise.reject(new Error('Authentication required. Please log in.'));
          }
        } else {
          // Development mode: use X-User-Id header ONLY
          const testUser = getCurrentTestUser();
          requestConfig.headers['X-User-Id'] = testUser.id;
        }

        return requestConfig;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response) {
          // Server responded with error
          const { status, data } = error.response;
          console.error(`API Error (${status}):`, data);

          // Handle specific error codes
          if (status === 401) {
            // Unauthorized - user needs to login again
            console.error('Unauthorized access - redirecting to login');
            // Trigger logout to clear invalid session
            window.dispatchEvent(new CustomEvent('auth:unauthorized'));
          }
        } else if (error.request) {
          // Request made but no response
          console.error('No response from server:', error.request);
        } else {
          // Something else happened
          console.error('Error setting up request:', error.message);
        }

        return Promise.reject(error);
      }
    );
  }

  // ========================================
  // Retry Logic with Exponential Backoff
  // ========================================

  /**
   * Determines if an error should be retried
   */
  shouldRetry(error) {
    // Don't retry if no error
    if (!error) return false;

    // Don't retry if response exists and status is 4xx (client error)
    // This includes 400, 401, 403, 404, 429, etc.
    if (error.response && error.response.status >= 400 && error.response.status < 500) {
      return false;
    }

    // Retry on network errors, timeouts, and 5xx server errors
    return (
      !error.response || // Network error
      error.code === 'ECONNABORTED' || // Timeout
      error.code === 'ETIMEDOUT' || // Timeout
      (error.response && error.response.status >= 500) // Server error
    );
  }

  /**
   * Execute a request with retry logic
   */
  async withRetry(requestFn, retries = this.maxRetries) {
    let lastError;

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error;

        // Check if we should retry
        if (!this.shouldRetry(error)) {
          throw error;
        }

        // Check if we have retries left
        if (attempt < retries) {
          // Calculate exponential backoff delay: 1s, 2s, 4s
          const delay = this.retryDelay * Math.pow(2, attempt);
          console.log(`Request failed, retrying in ${delay}ms... (attempt ${attempt + 1}/${retries})`);

          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries exhausted
    throw lastError;
  }

  // ========================================
  // User Interests API
  // ========================================

  /**
   * Get user's interests
   */
  async getInterests() {
    return this.withRetry(async () => {
      const response = await this.client.get('/users/interests');
      return response.data;
    });
  }

  /**
   * Create user's interests (onboarding)
   */
  async createInterests(interests, email, name, nativeLanguage) {
    return this.withRetry(async () => {
      const response = await this.client.post('/users/interests', {
        interests,
        email,
        name,
        nativeLanguage,
      });
      return response.data;
    });
  }

  /**
   * Update user's interests
   */
  async updateInterests(interests) {
    return this.withRetry(async () => {
      const response = await this.client.put('/users/interests', {
        interests,
      });
      return response.data;
    });
  }

  /**
   * Update user's complete profile (name + interests + language)
   */
  async updateProfile(name, interests, nativeLanguage) {
    return this.withRetry(async () => {
      const response = await this.client.put('/users/profile', {
        name,
        interests,
        nativeLanguage,
      });
      return response.data;
    });
  }

  async deleteAccount() {
    const accessToken = await cognitoService.getAccessToken();
    const response = await this.client.delete('/users/account', {
      headers: { 'X-Access-Token': accessToken },
    });
    return response.data;
  }

  // ========================================
  // Words API
  // ========================================

  /**
   * Get list of words with pagination and filtering
   */
  async getWords(params = {}) {
    return this.withRetry(async () => {
      const response = await this.client.get('/words', { params });
      return response.data;
    });
  }

  /**
   * Get a single word by ID
   */
  async getWord(wordId) {
    return this.withRetry(async () => {
      const response = await this.client.get(`/words/${encodeURIComponent(wordId)}`);
      return response.data;
    });
  }

  /**
   * Create a new word
   */
  async createWord(word, generateExamples = true, generateTranslation = true, learningLevel = 'learning') {
    return this.withRetry(async () => {
      const response = await this.client.post('/words', {
        word,
        generateExamples,
        generateTranslation,
        learningLevel,
      });
      return response.data;
    });
  }

  /**
   * Update a word (manual edit or regeneration)
   */
  async updateWord(wordId, data) {
    return this.withRetry(async () => {
      const response = await this.client.put(`/words/${encodeURIComponent(wordId)}`, data);
      return response.data;
    });
  }

  /**
   * Regenerate a single example for a word
   */
  async regenerateExample(wordId, exampleIndex) {
    return this.withRetry(async () => {
      const response = await this.client.put(`/words/${encodeURIComponent(wordId)}`, {
        regenerate: true,
        exampleIndex: exampleIndex,
      });
      return response.data;
    });
  }

  /**
   * Delete a word
   */
  async deleteWord(wordId) {
    return this.withRetry(async () => {
      const response = await this.client.delete(`/words/${encodeURIComponent(wordId)}`);
      return response.data;
    });
  }

  /**
   * Update a word's learning level (quick update)
   */
  async updateWordLearningLevel(wordId, learningLevel) {
    return this.withRetry(async () => {
      const response = await this.client.patch(
        `/words/${encodeURIComponent(wordId)}/learning-level`,
        { learningLevel }
      );
      return response.data;
    });
  }

  // ========================================
  // Chat Settings API (uses agent endpoint)
  // ========================================

  /**
   * Get chat settings and usage information
   * Returns data from agent endpoint in the format expected by ProfilePage
   */
  async getChatSettings() {
    return this.withRetry(async () => {
      const response = await this.client.get('/agent/settings');
      const data = response.data;

      // Transform to format expected by ProfilePage
      return {
        usage: data.usage,
        limits: data.limits,
        tier: data.tier,
        retentionDays: data.retentionDays
      };
    });
  }

  /**
   * Update chat retention settings
   */
  async updateChatSettings(retentionDays) {
    return this.withRetry(async () => {
      const response = await this.client.put('/agent/settings', {
        retentionDays,
      });
      return response.data;
    });
  }

  /**
   * Get chat messages with pagination
   */
  async getChatMessages(params = {}) {
    return this.withRetry(async () => {
      const queryParams = { ...params };
      if (queryParams.lastEvaluatedKey) {
        queryParams.lastEvaluatedKey = JSON.stringify(queryParams.lastEvaluatedKey);
      }

      const response = await this.client.get('/chat/messages', {
        params: queryParams,
      });
      return response.data;
    });
  }

  /**
   * Clear all chat messages
   */
  async clearChatMessages() {
    return this.withRetry(async () => {
      const response = await this.client.delete('/chat/messages');
      return response.data;
    });
  }

  // ========================================
  // Learning Agent API
  // ========================================

  /**
   * Get agent settings and learning statistics
   */
  async getAgentSettings() {
    return this.withRetry(async () => {
      const response = await this.client.get('/agent/settings');
      return response.data;
    });
  }

  /**
   * Update agent settings
   * @param {Object} settings - Settings to update
   * @param {number} settings.conversationContextSize - Context size (5-20)
   * @param {string} settings.preferredMode - Mode (adaptive, practice, quiz, free_chat)
   * @param {string} settings.encouragementLevel - Level (minimal, moderate, high)
   */
  async updateAgentSettings(settings) {
    return this.withRetry(async () => {
      const response = await this.client.put('/agent/settings', settings);
      return response.data;
    });
  }

  /**
   * Get learning statistics only
   */
  async getLearningStats() {
    return this.withRetry(async () => {
      const response = await this.client.get('/agent/stats');
      return response.data;
    });
  }

  /**
   * Send a message to the learning agent
   * @param {string} message - The user's message
   * @param {string} mode - Optional forced mode (practice, quiz, free_chat)
   */
  async sendAgentMessage(message, mode = null) {
    return this.withRetry(async () => {
      const body = { message };
      if (mode) {
        body.mode = mode;
      }
      const response = await this.client.post('/agent/chat', body, {
        timeout: 120000, // 2 minute timeout for agent
      });
      return response.data;
    });
  }

  /**
   * Send a message to the learning agent with streaming response
   * @param {string} message - The user's message
   * @param {string} mode - Optional forced mode
   * @param {Object} callbacks - Callback functions
   * @param {function} callbacks.onText - Callback for text chunks: (text) => void
   * @param {function} callbacks.onToolUse - Callback for tool use: (tool, input) => void
   * @param {function} callbacks.onStart - Callback when stream starts: (userMessage) => void
   * @param {function} callbacks.onDone - Callback when complete: (response) => void
   * @param {function} callbacks.onError - Callback on error: (error) => void
   */
  async sendAgentMessageStream(message, mode, { onText, onToolUse, onStart, onDone, onError }) {
    try {
      const headers = {
        'Content-Type': 'application/json',
      };

      // Add authentication headers (same logic as axios interceptor)
      if (config.features.authEnabled) {
        try {
          const token = await cognitoService.getIdToken();
          headers['Authorization'] = `Bearer ${token}`;
        } catch (error) {
          console.error('Failed to get auth token for agent streaming:', error);
          const authError = new Error('Authentication required. Please log in.');
          if (onError) {
            onError(authError);
          }
          throw authError;
        }
      } else {
        const testUser = getCurrentTestUser();
        headers['X-User-Id'] = testUser.id;
      }

      const body = { message };
      if (mode) {
        body.mode = mode;
      }

      const response = await fetch(`${this.baseURL}/agent/chat/stream`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorData;
        try {
          errorData = JSON.parse(errorText);
        } catch {
          errorData = { message: errorText };
        }
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      // Read the response body as a stream (NDJSON format)
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });

        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const event = JSON.parse(line);

            switch (event.type) {
              case 'start':
                if (onStart) {
                  onStart(event.userMessage);
                }
                break;

              case 'text':
                if (onText) {
                  onText(event.text);
                }
                break;

              case 'tool_use':
                if (onToolUse) {
                  onToolUse(event.tool, event.input);
                }
                break;

              case 'done':
                if (onDone) {
                  onDone({
                    assistantMessage: event.assistantMessage,
                    mode: event.mode,
                    activeWords: event.activeWords,
                    toolsUsed: event.toolsUsed,
                    learningStats: event.learningStats,
                    dailyUsageRemaining: event.dailyUsageRemaining,
                  });
                }
                break;

              case 'error':
                if (onError) {
                  onError(new Error(event.error));
                }
                break;

              default:
                console.warn('Unknown agent event type:', event.type);
            }
          } catch (parseError) {
            console.error('Failed to parse agent event:', line, parseError);
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const event = JSON.parse(buffer);
          if (event.type === 'done' && onDone) {
            onDone({
              assistantMessage: event.assistantMessage,
              mode: event.mode,
              activeWords: event.activeWords,
              toolsUsed: event.toolsUsed,
              learningStats: event.learningStats,
              dailyUsageRemaining: event.dailyUsageRemaining,
            });
          }
        } catch (err) {
          console.error('Failed to parse agent final buffer:', buffer, err);
        }
      }
    } catch (error) {
      if (onError) {
        onError(error);
      } else {
        throw error;
      }
    }
  }

  /**
   * Generate a new vocabulary exercise
   */
  async generateExercise(maxWords = 5, maxSentences = 10) {
    return this.withRetry(async () => {
      const response = await this.client.post('/exercise/generate', {
        maxWords,
        maxSentences,
      });
      return response.data;
    });
  }

  // ========================================
  // Utility Methods
  // ========================================

  /**
   * Set user ID (will be used when Cognito is implemented)
   */
  setUserId(userId) {
    this.userId = userId;
  }

  /**
   * Set authorization token (for future Cognito integration)
   */
  setAuthToken(token) {
    this.client.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  }

  /**
   * Clear authorization token
   */
  clearAuthToken() {
    delete this.client.defaults.headers.common['Authorization'];
  }

  // ========================================
  // Feedback API
  // ========================================

  async submitFeedback(content) {
    const response = await this.client.post('/feedback', { content });
    return response.data;
  }

  /**
   * Log a user event to CloudWatch for analytics.
   * Fire-and-forget — never throws or blocks the caller.
   */
  async logEvent(eventType, metadata = {}) {
    try {
      await this.client.post('/events', { eventType, metadata });
    } catch {
      // Intentionally silent — analytics must never affect UX
    }
  }
}

// Export singleton instance
const apiClient = new ApiClient();
export default apiClient;
